import React from 'react';

/**
 * NavigationLink - A flexible link component that supports both regular links and React Router
 * 
 * Usage:
 * 1. With regular links (default):
 *    <NavigationLink href="/path">Link Text</NavigationLink>
 * 
 * 2. With React Router Link component:
 *    import { Link } from 'react-router-dom';
 *    <NavigationLink href="/path" as={Link} to="/path">Link Text</NavigationLink>
 * 
 * 3. With React Router NavLink component:
 *    import { NavLink } from 'react-router-dom';
 *    <NavigationLink href="/path" as={NavLink} to="/path">Link Text</NavigationLink>
 */

export interface NavigationLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** The href for the link */
  href?: string;
  /** Custom component to render (e.g., React Router Link or NavLink) */
  as?: React.ElementType;
  /** Children to render */
  children: React.ReactNode;
  /** Additional props to pass to the custom component */
  [key: string]: any;
}

export const NavigationLink = React.forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  ({ as: Component = 'a', href, children, onClick, ...props }, ref) => {
    // If using React Router, the 'to' prop should be used instead of 'href'
    const linkProps = Component === 'a' 
      ? { href, ...props }
      : { to: href, ...props };

    return (
      <Component
        ref={ref}
        {...linkProps}
        onClick={onClick}
      >
        {children}
      </Component>
    );
  }
);

NavigationLink.displayName = 'NavigationLink';
